import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ImageCropperBanner } from '@/components/ui/image-cropper-banner';
import { useAuth } from '@/contexts/AuthContext';
import { useStorefrontAppearance } from '@/hooks/useStorefrontAppearance';
import { uploadImage, getExtensionForBlob } from '@/lib/image';
import { formatCnpj, isValidCnpj } from '@/lib/document';

const SOCIAL_FIELDS = [
  { key: 'facebook_url', label: 'Facebook', placeholder: 'https://facebook.com/sualoja' },
  { key: 'tiktok_url', label: 'TikTok', placeholder: 'https://tiktok.com/@sualoja' },
] as const;

export function StorefrontFooterContentManager() {
  const { user, updateUser } = useAuth();
  const { appearance, loading, save } = useStorefrontAppearance(user?.id, 'eletronicos');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoCropperOpen, setLogoCropperOpen] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [cnpjInput, setCnpjInput] = useState(appearance.footer_cnpj ?? '');
  const [cnpjError, setCnpjError] = useState(false);

  useEffect(() => setCnpjInput(appearance.footer_cnpj ?? ''), [appearance.footer_cnpj]);

  if (loading) return null;

  const handleCnpjBlur = () => {
    const digits = cnpjInput.replace(/\D/g, '');
    if (!digits) {
      setCnpjError(false);
      if (appearance.footer_cnpj) save({ footer_cnpj: null });
      return;
    }
    const valid = isValidCnpj(digits);
    setCnpjError(!valid);
    if (valid) save({ footer_cnpj: cnpjInput });
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }
    setSelectedLogoFile(file);
    setLogoCropperOpen(true);
  };

  const handleLogoCropComplete = async (croppedBlob: Blob) => {
    if (!user?.id) return;
    try {
      setUploadingLogo(true);
      setLogoCropperOpen(false);
      const file = new File([croppedBlob], selectedLogoFile?.name || `footer-logo.${getExtensionForBlob(croppedBlob)}`, { type: croppedBlob.type });
      const url = await uploadImage(file, user.id, 'theme-footer-logo');
      await save({ footer_logo_url: url });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar imagem');
    } finally {
      setUploadingLogo(false);
      setSelectedLogoFile(null);
    }
  };

  const handleSocialBlur = async (key: (typeof SOCIAL_FIELDS)[number]['key'], value: string) => {
    const normalized = value.trim() || null;
    if (normalized === (user?.[key] ?? null)) return;
    await updateUser({ [key]: normalized });
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Logo do rodapé (opcional)</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Substitui o nome da loja por uma imagem no rodapé. Pode ser diferente da logo do cabeçalho.
        </p>
        {appearance.footer_logo_url && (
          <div className="rounded p-3 mb-2 inline-block border bg-muted/30">
            <img src={appearance.footer_logo_url} alt="" className="h-10 w-auto object-contain" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="file"
            id="footer-logo-upload"
            accept="image/*"
            className="hidden"
            disabled={uploadingLogo}
            onChange={handleLogoFileChange}
          />
          <label htmlFor="footer-logo-upload">
            <Button type="button" variant="outline" size="sm" disabled={uploadingLogo} asChild>
              <span>
                {uploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {appearance.footer_logo_url ? 'Trocar' : 'Enviar logo'}
              </span>
            </Button>
          </label>
          {appearance.footer_logo_url && (
            <Button type="button" variant="ghost" size="sm" onClick={() => save({ footer_logo_url: null })}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remover
            </Button>
          )}
        </div>
      </div>

      {logoCropperOpen && selectedLogoFile && (
        <ImageCropperBanner
          image={URL.createObjectURL(selectedLogoFile)}
          onCrop={handleLogoCropComplete}
          onCancel={() => {
            setLogoCropperOpen(false);
            setSelectedLogoFile(null);
          }}
          open={logoCropperOpen}
          aspectRatio={3}
        />
      )}

      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">Frase abaixo do nome/logo</Label>
        <Input
          defaultValue={appearance.footer_tagline ?? ''}
          onBlur={(e) => save({ footer_tagline: e.target.value || null })}
          placeholder={user?.bio || 'Usa a bio do perfil por padrão'}
          maxLength={160}
        />
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">Dados da empresa (opcional)</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Aparecem juntos, abaixo da frase, no rodapé da loja.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Nome da empresa</Label>
            <Input
              defaultValue={appearance.footer_company_name ?? ''}
              onBlur={(e) => save({ footer_company_name: e.target.value.trim() || null })}
              placeholder="Razão social ou nome fantasia"
              maxLength={160}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">CNPJ</Label>
            <Input
              value={cnpjInput}
              onChange={(e) => setCnpjInput(formatCnpj(e.target.value))}
              onBlur={handleCnpjBlur}
              placeholder="00.000.000/0001-00"
              maxLength={18}
            />
            {cnpjError && <p className="text-xs text-destructive mt-1">CNPJ inválido — confira os números.</p>}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">Redes sociais</Label>
        <p className="text-xs text-muted-foreground mb-2">
          O Instagram já usa o que está configurado no seu perfil. Preencha aqui Facebook e TikTok — o ícone só
          aparece no rodapé se o campo estiver preenchido.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
              <Input
                defaultValue={user?.[key] || ''}
                onBlur={(e) => handleSocialBlur(key, e.target.value)}
                placeholder={placeholder}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Coluna "Categorias"</span>
          <Switch
            checked={appearance.footer_categories_enabled}
            onCheckedChange={(v) => save({ footer_categories_enabled: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Coluna "Atendimento"</span>
          <Switch
            checked={appearance.footer_contact_enabled}
            onCheckedChange={(v) => save({ footer_contact_enabled: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Coluna "Formas de pagamento" / Selos</span>
          <Switch
            checked={appearance.footer_payment_enabled}
            onCheckedChange={(v) => save({ footer_payment_enabled: v })}
          />
        </div>
      </div>
    </div>
  );
}
